import { AUDIO_CONFIGS } from 'Modules/softphone/constants';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { audioLogger as logger } from 'Modules/softphone/utils/logger';
import React, { useEffect, useRef } from 'react';

/**
 * SoftphoneAudios Component
 *
 * Renders audio elements in the DOM to bypass browser autoplay restrictions.
 *
 * CRITICAL: Audio elements created via `new Audio()` in JavaScript are subject to
 * stricter autoplay policies. By rendering them in the DOM with preload="auto",
 * we ensure they can play even when the window doesn't have focus.
 *
 * This matches the legacy softphone behavior where audio elements are always
 * present in the DOM.
 *
 * LIFECYCLE: This component should remain mounted for the entire softphone session
 * to ensure audio elements are always available. Unmounting during active calls
 * will cause audio to stop working.
 */
export const SoftphoneAudios: React.FC = () => {
  const ringtoneRef = useRef<HTMLAudioElement>(null);
  const ringbackRef = useRef<HTMLAudioElement>(null);
  const secondIncomingRef = useRef<HTMLAudioElement>(null);
  const lostConnectionRef = useRef<HTMLAudioElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const isRegistered = useRef(false);

  useEffect(() => {
    // Prevent re-registration if already registered
    if (isRegistered.current) {
      logger.debug('Audio elements already registered, skipping');
      return;
    }

    const audioService = AudioService.getInstance();

    // Register pre-created audio elements with AudioService
    // This allows AudioService to use these DOM elements instead of creating new ones
    if (ringtoneRef.current) {
      audioService.registerAudioElement('ringtone', ringtoneRef.current);
      logger.debug('Registered ringtone audio element');
    }

    if (ringbackRef.current) {
      audioService.registerAudioElement('ringback', ringbackRef.current);
      logger.debug('Registered ringback audio element');
    }

    if (secondIncomingRef.current) {
      audioService.registerAudioElement('secondIncoming', secondIncomingRef.current);
      logger.debug('Registered secondIncoming audio element');
    }

    if (lostConnectionRef.current) {
      audioService.registerAudioElement('lostConnection', lostConnectionRef.current);
      logger.debug('Registered lostConnection audio element');
    }

    if (remoteAudioRef.current) {
      audioService.registerAudioElement('remoteAudio', remoteAudioRef.current);
      logger.debug('Registered remoteAudio audio element');
    }

    isRegistered.current = true;
    logger.info('All audio elements registered with AudioService');

    // Cleanup: Mark as unregistered when component unmounts
    return () => {
      isRegistered.current = false;
      logger.debug('SoftphoneAudios unmounting, marking as unregistered');
    };
  }, []);

  return (
    <>
      {/* Ringtone - plays when receiving incoming calls */}
      <audio
        ref={ringtoneRef}
        id="softphone-ringtone"
        className="softphone-audio"
        src={AUDIO_CONFIGS.ringtone.src}
        preload="auto"
        loop={AUDIO_CONFIGS.ringtone.loop || undefined}
      />

      {/* Ringback - plays when making outgoing calls */}
      <audio
        ref={ringbackRef}
        id="softphone-ringback"
        className="softphone-audio"
        src={AUDIO_CONFIGS.ringback.src}
        preload="auto"
        loop={AUDIO_CONFIGS.ringback.loop || undefined}
      />

      {/* Second Incoming - plays when receiving a call while on another call */}
      <audio
        ref={secondIncomingRef}
        id="softphone-secondIncoming"
        className="softphone-audio"
        src={AUDIO_CONFIGS.secondIncoming.src}
        preload="auto"
        loop={AUDIO_CONFIGS.secondIncoming.loop || undefined}
      />

      {/* Lost Connection - plays when connection is lost during a call */}
      <audio
        ref={lostConnectionRef}
        id="softphone-lostConnection"
        className="softphone-audio"
        src={AUDIO_CONFIGS.lostConnection.src}
        preload="auto"
        loop={AUDIO_CONFIGS.lostConnection.loop || undefined}
      />

      {/* Remote Audio - plays the remote party's audio during a call */}
      <audio
        ref={remoteAudioRef}
        id="softphone-remoteAudio"
        className="softphone-audio"
        autoPlay
      />
    </>
  );
};
